import React from "react";

/**
 * Destineze Brand Logo system
 * --------------------------------------------------------------
 * Concept — "The Scholar's Globe":
 *   • Mortarboard with tassel  (academia / education)
 *   • Globe with longitude–latitude grid (global / educational tourism)
 *   • Three student silhouettes standing on the globe (institutional cohort,
 *     learning, and "students at the destination")
 *   • Heraldic double-ring (institutional crest)
 *
 * Slogan: "Beyond Borders. Beyond Classrooms."
 */

export const BRAND_SLOGAN = "Beyond Borders. Beyond Classrooms.";

const PALETTES = {
  primary:    { bg: "#0A192F", gold: "#D4AF37", soft: "#0A192F", ringSoft: "rgba(212,175,55,0.30)", ringMain: "rgba(212,175,55,0.55)", lat: "rgba(212,175,55,0.7)" },
  ghostDark:  { bg: "transparent", gold: "#D4AF37", soft: "#0A192F", ringSoft: "rgba(212,175,55,0.30)", ringMain: "rgba(212,175,55,0.55)", lat: "rgba(212,175,55,0.7)" },
  ghostLight: { bg: "transparent", gold: "#0A192F", soft: "#FFFFFF", ringSoft: "rgba(10,25,47,0.25)", ringMain: "rgba(10,25,47,0.55)", lat: "rgba(10,25,47,0.7)" },
  inverse:    { bg: "#D4AF37", gold: "#0A192F", soft: "#D4AF37", ringSoft: "rgba(10,25,47,0.30)", ringMain: "rgba(10,25,47,0.55)", lat: "rgba(10,25,47,0.7)" },
};

export const LogoMark = React.forwardRef(function LogoMark(
  { size = 44, variant = "primary", rounded = true, className = "", ...rest },
  ref
) {
  const c = PALETTES[variant] || PALETTES.primary;
  const showBg = variant === "primary" || variant === "inverse";

  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 200 200"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Destineze"
      className={className}
      {...rest}
    >
      {showBg ? (
        <rect
          width="200"
          height="200"
          rx={rounded ? 18 : 0}
          fill={c.bg}
        />
      ) : null}

      {/* Heraldic double-ring */}
      <circle cx="100" cy="100" r="93" fill="none" stroke={c.ringSoft} strokeWidth="0.7" />
      <circle cx="100" cy="100" r="89" fill="none" stroke={c.ringMain} strokeWidth="1.2" />

      {/* ===== MORTARBOARD ===== */}
      <polygon points="100,38 162,57 100,76 38,57" fill={c.gold} />
      <polyline
        points="100,42 158,57 100,72 42,57 100,42"
        fill="none"
        stroke={c.soft}
        strokeOpacity="0.25"
        strokeWidth="0.8"
      />
      <path d="M 70 73 L 130 73 L 122 84 L 78 84 Z" fill={c.gold} />
      <line x1="78" y1="84" x2="122" y2="84" stroke={c.soft} strokeOpacity="0.3" strokeWidth="0.8" />
      <circle cx="100" cy="57" r="2.6" fill={c.soft} fillOpacity="0.9" />

      {/* Tassel */}
      <path
        d="M 100 57 Q 132 70 138 90"
        fill="none"
        stroke={c.gold}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <g transform="translate(138 91)">
        <circle r="2.8" fill={c.gold} />
        <line x1="-3" y1="0" x2="-3" y2="6.5" stroke={c.gold} strokeWidth="0.8" strokeLinecap="round" />
        <line x1="0"  y1="0" x2="0"  y2="7.5" stroke={c.gold} strokeWidth="0.8" strokeLinecap="round" />
        <line x1="3"  y1="0" x2="3"  y2="6.5" stroke={c.gold} strokeWidth="0.8" strokeLinecap="round" />
      </g>

      {/* ===== GLOBE ===== */}
      <circle cx="100" cy="132" r="34" fill="none" stroke={c.gold} strokeWidth="2" />
      <line x1="66" y1="132" x2="134" y2="132" stroke={c.gold} strokeWidth="1" />
      <path d="M 72 117 Q 100 114 128 117" fill="none" stroke={c.lat} strokeWidth="0.9" />
      <path d="M 72 147 Q 100 150 128 147" fill="none" stroke={c.lat} strokeWidth="0.9" />
      <line x1="100" y1="98" x2="100" y2="166" stroke={c.gold} strokeWidth="1" />
      <ellipse cx="100" cy="132" rx="13" ry="34" fill="none" stroke={c.lat} strokeWidth="0.9" />

      {/* Destination marker (top-right of globe, above the cohort) */}
      <circle cx="120" cy="116" r="2.6" fill={c.gold} />

      {/* ===== STUDENT COHORT (3 silhouettes standing on the globe) ===== */}
      {/* Left student */}
      <g fill={c.gold}>
        <circle cx="83" cy="138" r="3.2" />
        <path d="M 83 142 Q 76 144 75 152 L 75 161 Q 79 162 83 162 Q 87 162 91 161 L 91 152 Q 90 144 83 142 Z" />
      </g>
      {/* Center student (slightly taller) */}
      <g fill={c.gold}>
        <circle cx="100" cy="134" r="4" />
        <path d="M 100 139 Q 91 141 89 152 L 89 163 Q 95 165 100 165 Q 105 165 111 163 L 111 152 Q 109 141 100 139 Z" />
      </g>
      {/* Right student */}
      <g fill={c.gold}>
        <circle cx="117" cy="138" r="3.2" />
        <path d="M 117 142 Q 110 144 109 152 L 109 161 Q 113 162 117 162 Q 121 162 125 161 L 125 152 Q 124 144 117 142 Z" />
      </g>
    </svg>
  );
});

/** Backwards-compatible alias */
export const LogoEmblem = LogoMark;

/** Slogan badge */
export const SloganBadge = ({ tone = "light", className = "", ...rest }) => {
  const color =
    tone === "light"
      ? "text-gold border-gold/30 bg-white/[0.03]"
      : "text-gold-500 border-gold-500/30 bg-white";
  return (
    <span
      data-testid="brand-slogan"
      className={`inline-flex items-center gap-2 px-4 py-2 border text-[11px] uppercase tracking-[0.28em] ${color} ${className}`}
      {...rest}
    >
      <span aria-hidden="true">✦</span>
      {BRAND_SLOGAN}
    </span>
  );
};

export default LogoMark;
